import useTrackEvent from '@/application/write/analytics/useTrackEvent';
import Button from '@/components/elements/Button';
import Price from '@/components/elements/Price';
import VaultIcon from '@/components/elements/VaultIcon';
import Modal from '@/components/modules/Modal';
import { useModalContext } from '@/presentation/context/modal';
import { getCollectionPath } from '@/presentation/utils/paths';
import Link from 'next/link';
import { Vault, Zero } from 'nftx.js';
import { useTranslations } from 'use-intl';
import Collapsible from '../../modules/Collapsible';
import Icon, { Icons } from '../../elements/Icon';

export interface CreateVaultExistsModalProps {
  vaults: Pick<
    Vault,
    'vaultId' | 'token' | 'prices' | 'totalHoldings' | 'slug' | 'collectionSlug'
  >[];
  chain: string;
  onCancel: () => void;
  onProceed: () => void;
}

export const CreateVaultExistsModal = ({
  vaults,
  chain,
  onCancel,
  onProceed,
}: CreateVaultExistsModalProps) => {
  const t = useTranslations('CreateVault.ExistsModal');
  const trackEvent = useTrackEvent();
  const { showCreatePositionModal } = useModalContext();

  return (
    <Modal
      size="xl"
      clickOffToClose={false}
      showCloseButton={false}
      justify="center"
    >
      <div className="p-6 space-y-4">
        <h1 className="text-xl mb-4 font-bold">{t('title')}</h1>
        <section className="divide-y divide-gray-300">
          {vaults.map((vault) => (
            <div key={vault.vaultId} className="flex justify-between py-2">
              <div className="flex items-center">
                <VaultIcon className="w-6 h-6 mr-2" vaultId={vault.vaultId} />
                <div>
                  <h4 className="mr-4 text-sm">{vault.token.symbol}</h4>
                  <h5 className="mr-5 text-xs">{vault.token.name}</h5>
                </div>
              </div>
              <div className="flex items-center text-right">
                <div className="inline-flex flex-col uppercase mr-6">
                  <span className="font-medium text-xxs text-gray-500">
                    {t('spotPrice')}
                  </span>
                  <span className="text-sm">
                    <Price
                      value={vault.prices?.[0]?.redeem?.vTokenPrice ?? Zero}
                    />
                  </span>
                </div>
                <div className="inline-flex flex-col uppercase mr-6">
                  <span className="font-medium text-xxs text-gray-500">
                    {'Items'}
                  </span>
                  <span className="text-sm">{vault.totalHoldings}</span>
                </div>
                <div className="inline-flex space-x-4 items-center">
                  <Link
                    href={getCollectionPath({
                      chain,
                      collectionSlug: vault.collectionSlug,
                      tab: 'info',
                      vaultSlug: vault.slug,
                    })}
                    target="_blank"
                    className="text-pink-500 font-medium text-sm hover:underline"
                    onClick={() =>
                      trackEvent('Create Vault: Existing Vault Decision', {
                        action: 'View Existing Vault',
                      })
                    }
                  >
                    {t('seeVault')}
                  </Link>
                  <button
                    type="button"
                    className="text-pink-500 font-medium text-sm hover:underline"
                    onClick={() => {
                      trackEvent('Create Vault: Existing Vault Decision', {
                        action: 'Deposit Existing Vault',
                      });
                      showCreatePositionModal({
                        vaultId: vault.vaultId,
                        isNewVault: false,
                      });
                    }}
                  >
                    {t('deposit')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </section>
        <Collapsible
          header={
            <h3 className="flex items-center font-medium text-sm">
              <Icon
                name={Icons.QUESTION_MARK_CIRCLE}
                size={20}
                className="mr-2 text-pink-500"
              />
              {'Create a new vault or deposit to an existing one'}
            </h3>
          }
        >
          <div className="p-4 font-normal">
            <h5 className="font-bold">{'Depositing to an Existing Vault'}</h5>
            <dl>
              <dt className="font-medium text-teal-500">{'Pros'}</dt>
              <dd>
                {
                  'Best for integrating into existing market conditions with minimal setup. Add NFTs to the vault using current prices and set your position in the existing pool. Only requires gas fees for creating your position, not for vault or pool creation.'
                }
              </dd>
            </dl>
            <h5 className="font-bold mt-4">{'Creating a New Vault:'}</h5>
            <dl className="">
              <dt className="font-medium text-teal-500">{'Pros'}</dt>
              <dd>
                {
                  'Ideal for tailored eligibility NFT collections, like specific traits (e.g., 2017 mooncats, Hoodie CryptoPunks). Full control over vault settings: name, token ticker, price, and vault fees.'
                }
              </dd>
              <dt className="font-medium mt-4 text-red-500">{'Cons'}</dt>
              <dd>
                {
                  'Additional gas fees for vault creation, initial pool setup, and position establishment. Diluted liquidity across vaults result in larger price impacts.'
                }
              </dd>
            </dl>
          </div>
        </Collapsible>
        <footer className="mt-6 sm:flex sm:space-x-4">
          <Button variant={'primary'} onClick={onCancel} className="flex-1">
            {t('cancel')}
          </Button>
          <Button onClick={onProceed} className="flex-1">
            {t('proceed')}
          </Button>
        </footer>
      </div>
    </Modal>
  );
};

export default CreateVaultExistsModal;
